import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import { logException } from "@punchup/sentry";
import { bucketReferrer } from "./referrer-buckets";
import { isExcludedReferrer } from "./exclusions";
import { ComedianSourceCounts } from "../../models/comedian-weekly-digest";

const PAGE_SIZE = 10000;

/**
 * Fetches follow events for a week range and returns per-comedian,
 * per-source-bucket sets of unique follower distinct_ids.
 *
 * Note: events_followed_comedian does not have ComedianName, so the
 * non-comedian name filter cannot be applied here. Follow events are
 * generated by the app's follow action which only fires on real comedian
 * pages, so non-comedian entries are not expected in this table.
 */
export async function fetchFollowEventsBySource(
  supabase: SupabaseClient<Database>,
  weekStart: string,
  weekEnd: string
): Promise<ComedianSourceCounts> {
  const result: ComedianSourceCounts = new Map();
  let offset = 0;

  while (true) {
    const { data, error } = await supabase
      .from("events_followed_comedian")
      .select("*")
      .gte("timestamp", weekStart)
      .lt("timestamp", weekEnd)
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) {
      logException(error, {
        data: "[comedian-weekly-digest] Failed to fetch follow events",
      });
      throw error;
    }

    if (!data || data.length === 0) break;

    for (const row of data) {
      if (!row.ComedianId || !row.distinct_id) continue;
      if (
        isExcludedReferrer(
          row["$initial_referring_domain"] as string | null | undefined
        )
      )
        continue;

      const bucket = bucketReferrer(
        row["$initial_referring_domain"],
        row["$initial_referrer"]
      );

      let comedianMap = result.get(row.ComedianId);
      if (!comedianMap) {
        comedianMap = new Map();
        result.set(row.ComedianId, comedianMap);
      }

      let userSet = comedianMap.get(bucket);
      if (!userSet) {
        userSet = new Set();
        comedianMap.set(bucket, userSet);
      }

      userSet.add(row.distinct_id);
    }

    offset += data.length;
    if (data.length < PAGE_SIZE) break;
  }

  return result;
}
